import React from 'react';
import useMediaQuery from "@mui/system/useMediaQuery";
import {Box, CircularProgress, Typography, useTheme} from "@mui/joy";
import Dialog from "@mui/material/Dialog";
import {BasicModalContent, EmptyView} from "@dreebit-registry/vsm-microservice-components";
import {Button} from "@mui/material";
import {resolveErrorMessage} from "../../../utils/resolveErrorMessage";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "@apollo/client";
import {toast} from "react-toastify";
import _ from "lodash";
import {CreateGrid} from "../../../graphql/mutations/createGrid";
import {GridListTDAs} from "../../../graphql/queries/gridListTDAs";
import {GridViewClient} from "../../../graphql/queries/gridViewClient";
import {GridClientRootNode, GridVisibleRootCount} from "../../../graphql/queries/gridClientRootNode";
import InstalledBaseTDAForm from "../../InstalledBaseTDAForm";
import {useNavigate} from "react-router-dom";
import NodeSelectionHint from "./NodeSelectionHint";

interface Props {
    gridViewId: string
    open: boolean,
    onClose?: () => void,
}

const CreateGridModal = ({gridViewId, onClose, open}: Props) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const {t} = useTranslation();

    const [createGridMutation] = useMutation(CreateGrid);

    const {data} = useQuery(GridListTDAs, {
        variables: {
            typeDefinitionIndex: "GRID_CREATE"
        },
        fetchPolicy: "network-only",
    })

    // Scope the node picker to the grid view's client: resolve the client's
    // installed base root node (the schema has no direct client→root-node
    // link, see GridClientRootNode). Only a grid view without client browses
    // unrestricted; a failed lookup shows an error instead of falling back to
    // the full company list.
    const gridViewClientQuery = useQuery(GridViewClient, {
        variables: {id: gridViewId},
        notifyOnNetworkStatusChange: true,
    })
    const clientId = gridViewClientQuery.data?.gridView?.client?.id
    const clientResolved = !gridViewClientQuery.loading && !gridViewClientQuery.error
    // Follows GridViewClient: a direct lookup of the client's root instead of
    // the full root list (thousands of companies for a global admin).
    const clientRootNodeQuery = useQuery(GridClientRootNode, {
        variables: {clientId},
        skip: !clientResolved || !clientId,
        notifyOnNetworkStatusChange: true,
    })
    const visibleRootCountQuery = useQuery(GridVisibleRootCount, {
        skip: !clientResolved || !!clientId,
        notifyOnNetworkStatusChange: true,
    })
    const scopeQuery = clientId ? clientRootNodeQuery : visibleRootCountQuery
    const clientRootNode = _.get(clientRootNodeQuery, 'data.installedBaseNodeList.installedBaseNodes[0]')
    const clientRootNodeId = clientRootNode?.id

    // The picker reads rootNodeId once at mount, so the form only mounts once
    // the scope is settled ('ready'), never unrestricted first.
    const scopeState: 'loading' | 'error' | 'noVisibleNodes' | 'emptyInstalledBase' | 'ready' = (() => {
        if (gridViewClientQuery.loading) return 'loading'
        if (gridViewClientQuery.error || scopeQuery.error) return 'error'
        // A skipped query reports loading: false before its first request.
        if (scopeQuery.loading || !scopeQuery.data) return 'loading'
        // The user sees no node of the grid view's company (or none at all for
        // a grid view without company).
        if (clientId ? !clientRootNode : _.get(visibleRootCountQuery, 'data.installedBaseRootNodeList.total') === 0) return 'noVisibleNodes'
        // The company's installed base exists but is still empty: a grid needs
        // nodes below the selected one, so the browser would offer nothing useful.
        if (clientRootNode?.childrenNodeCount === 0) return 'emptyInstalledBase'
        return 'ready'
    })()

    const retryScope = () => {
        if (gridViewClientQuery.error) gridViewClientQuery.refetch()
        else if (scopeQuery.error) scopeQuery.refetch()
    }

    const handleSubmit = (input: any) => {
        return createGridMutation({
                variables: {
                    input: input,
                    gridViewId: gridViewId
                },
                refetchQueries: ["GridList"]
            }
        ).then((res) => {
            const gridId = res.data?.createGrid?.id
            if (onClose) {
                onClose();
            }

            if (gridId) {
                toast.success(`${t("Successfully created grid system")}`);
                navigate(`/gridViews/${gridViewId}/grids/${gridId}`)
            } else {
                toast.error(t("Error creating grid."))
            }
        }).catch((err) => {
            toast.error(resolveErrorMessage(err, t))
        })
    }

    let attributes = _.get(data, 'gridList.typeDefinitionAttributeList.typeDefinitionAttributes', [])
    attributes = attributes.map((a: any) => {
        if (a.index === "azimuth") {
            return {
                ...a,
                hint: t("Compass rotation in degrees (0–360°, 0° = North)"),
            }
        } else {
            return a
        }
    })

    return <Dialog open={open} onClose={onClose} fullScreen={isFullScreen}>
        <BasicModalContent onClose={onClose} title={<Typography level={'titleSmallLight'}>{t('Create system grid')}</Typography>}>
            {scopeState === 'loading' &&
                <Box data-testid="grid-scope-loading" display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap="12px" minHeight="240px">
                    <CircularProgress size="md"/>
                    <Typography level="body" color="neutral">
                        {t("Loading installed base…")}
                    </Typography>
                </Box>
            }
            {scopeState === 'error' &&
                <Box data-testid="grid-scope-error" display="flex" flexDirection="column" alignItems="center" gap="12px" sx={{pt: '8px'}}>
                    <EmptyView width={260} title={t("The installed base could not be loaded.")}/>
                    <Box display="flex" gap="12px" width="100%">
                        <Button variant="outlined" fullWidth onClick={onClose}>
                            {t("Close")}
                        </Button>
                        <Button variant="contained" fullWidth onClick={retryScope}>
                            {t("Try again")}
                        </Button>
                    </Box>
                </Box>
            }
            {scopeState === 'noVisibleNodes' &&
                <Box data-testid="grid-no-visible-nodes" display="flex" flexDirection="column" alignItems="center" gap="12px" sx={{pt: '8px'}}>
                    <EmptyView noAccess width={260} title={t("You can't see any installed base nodes of this company.")}/>
                    <Typography level="body" color="neutral" textAlign="center">
                        {t("Ask an administrator to grant you access to the installed base.")}
                    </Typography>
                    <Button variant="outlined" fullWidth onClick={onClose}>
                        {t("Close")}
                    </Button>
                </Box>
            }
            {scopeState === 'emptyInstalledBase' &&
                <Box data-testid="grid-empty-installed-base" display="flex" flexDirection="column" alignItems="center" gap="12px" sx={{pt: '8px'}}>
                    <EmptyView width={260} title={t("The installed base of {{company}} is still empty.", {company: clientRootNode?.title})}/>
                    <Typography level="body" color="neutral" textAlign="center">
                        {t("Create a system with its pressure lines and assets in the installed base first, then add it here as a grid.")}
                    </Typography>
                    <Box display="flex" gap="12px" width="100%">
                        <Button variant="outlined" fullWidth onClick={onClose}>
                            {t("Close")}
                        </Button>
                        <Button variant="contained" fullWidth onClick={() => navigate(`/installedBase/${clientRootNodeId}`)}>
                            {t("Open installed base")}
                        </Button>
                    </Box>
                </Box>
            }
            {scopeState === 'ready' && <>
                <NodeSelectionHint/>
                <InstalledBaseTDAForm
                    formStyle={{marginTop: '-16px'}}
                    onSubmit={handleSubmit}
                    onCancel={onClose}
                    submitButtonText={t('Create')}
                    attributes={attributes}
                    nodePickerRootNodeId={clientRootNodeId ? String(clientRootNodeId) : undefined}
                    nodePickerVariant="browser"
                />
            </>}
        </BasicModalContent>
    </Dialog>
};

export default CreateGridModal;
